/**
 * Plain Text Tab Component
 *
 * Textarea for composing plain text emails with formatting toolbar.
 * Used in the email reply panel.
 */

import React, { useState } from "react";
import { PlainTextTabProps } from "./types";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaListUl,
  FaListOl,
  FaLink,
  FaPaperclip,
} from "react-icons/fa";
import { HiOutlineEmojiHappy } from "react-icons/hi";
import { MdFormatColorText } from "react-icons/md";

export const PlainTextTab: React.FC<PlainTextTabProps> = ({
  value,
  onChange,
}) => {
  const [activeFormats, setActiveFormats] = useState<string[]>([]);

  const toggleFormat = (format: string) => {
    setActiveFormats((prev) =>
      prev.includes(format)
        ? prev.filter((f) => f !== format)
        : [...prev, format]
    );
  };

  const isActive = (format: string) => activeFormats.includes(format);

  return (
    <div className="px-5 py-4">
      {/* Body Label */}
      <label className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2 block">Body:</label>

      {/* Text Area Container with Border */}
      <div className="border border-[#7C83FD] dark:border-indigo-400 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
        {/* Textarea */}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your message here..."
          className="w-full p-4 text-[13px] text-gray-700 dark:text-gray-200 border-none outline-none resize-none bg-white dark:bg-gray-800 leading-relaxed"
          style={{ minHeight: "200px" }}
        />

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          {/* Left Icons - Link, Attachment, Emoji */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-[#7C83FD] dark:hover:text-indigo-400 transition-colors rounded"
              title="Insert Link"
            >
              <FaLink size={14} />
            </button>
            <button
              type="button"
              className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-[#7C83FD] dark:hover:text-indigo-400 transition-colors rounded"
              title="Attach File"
            >
              <FaPaperclip size={14} />
            </button>
            <button
              type="button"
              className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-[#7C83FD] dark:hover:text-indigo-400 transition-colors rounded"
              title="Insert Emoji"
            >
              <HiOutlineEmojiHappy size={16} />
            </button>
          </div>

          {/* Center/Right Icons - Formatting */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggleFormat("bold")}
              className={`p-1.5 transition-colors rounded ${
                isActive("bold")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Bold"
            >
              <FaBold size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat("italic")}
              className={`p-1.5 transition-colors rounded ${
                isActive("italic")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Italic"
            >
              <FaItalic size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat("underline")}
              className={`p-1.5 transition-colors rounded ${
                isActive("underline")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Underline"
            >
              <FaUnderline size={12} />
            </button>
            <button
              type="button"
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400 transition-colors rounded"
              title="Text Color"
            >
              <MdFormatColorText size={14} />
            </button>

            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

            <button
              type="button"
              onClick={() => toggleFormat("align-left")}
              className={`p-1.5 transition-colors rounded ${
                isActive("align-left")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Align Left"
            >
              <FaAlignLeft size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat("align-center")}
              className={`p-1.5 transition-colors rounded ${
                isActive("align-center")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Align Center"
            >
              <FaAlignCenter size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat("align-right")}
              className={`p-1.5 transition-colors rounded ${
                isActive("align-right")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Align Right"
            >
              <FaAlignRight size={12} />
            </button>

            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

            <button
              type="button"
              onClick={() => toggleFormat("list-ul")}
              className={`p-1.5 transition-colors rounded ${
                isActive("list-ul")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Bullet List"
            >
              <FaListUl size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat("list-ol")}
              className={`p-1.5 transition-colors rounded ${
                isActive("list-ol")
                  ? "text-[#7C83FD] dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15"
                  : "text-gray-500 dark:text-gray-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
              }`}
              title="Numbered List"
            >
              <FaListOl size={12} />
            </button>

            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

            {/* ConvoAI Button */}
            <button
              type="button"
              className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium text-[#7C83FD] dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 rounded transition-colors"
              title="ConvoAI"
            >
              <div className="w-5 h-5 rounded-full bg-[#7C83FD] flex items-center justify-center">
                <span className="text-white text-[10px]">AI</span>
              </div>
              <span>ConvoAI</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
